import { castDraft, original, produce } from "immer";
import { partition } from "lodash";

import { GridRowId } from "../idTypeBrands.js";
import { setDifference } from "../utils/setUtils.js";

import { GridElementLocator, getGridElement } from "./gridElementLocator.js";
import {
  getGaplessColumns,
  getSortedAndValidatedColumns,
  isApproximatelyEquallySizedDenseRow,
  makeEqualDenseRow,
} from "./gridRowUtils.js";
import { GridColumn, GridElement, GridRow } from "./gridTypes.js";

/** Removes an element, and removes its column if now empty, from the given GridLayout */
export const removeElementAndEmptyColumn = (
  gridRows: readonly GridRow[],
  locator: GridElementLocator,
): {
  newGridRows: readonly GridRow[];
  modifiedRowId?: GridRowId;
  removedElement?: GridElement;
  removedColumn?: GridColumn;
} => {
  let modifiedRowId: GridRowId | undefined;
  let removedElement: GridElement | undefined;
  let removedColumn: GridColumn | undefined;

  const newGridRows = produce(gridRows, (draft) => {
    const locationInfo = getGridElement(draft, locator);
    if (locationInfo != null) {
      const { column, columnIdx, elementIdx, row } = locationInfo;
      const sortedColumns = getSortedAndValidatedColumns(row.gridColumns);
      const gaplessColumns = getGaplessColumns(sortedColumns);
      const isEqualDenseRow =
        isApproximatelyEquallySizedDenseRow(gaplessColumns);

      removedElement = original(column.gridElements.splice(elementIdx, 1)[0]);
      modifiedRowId = row.id;

      if (column.gridElements.length === 0) {
        removedColumn = original(row.gridColumns.splice(columnIdx, 1)[0]);

        // Resize remaining columns to still fill the row if the row was previously equally-sized
        if (isEqualDenseRow) {
          row.gridColumns = castDraft(makeEqualDenseRow(row.gridColumns));
        }
      }
    } else {
      console.warn(
        `Tried to remove GridElement ${locator.elementId} from row ${locator.rowId} but it wasn't found.`,
      );
    }
  });
  return { newGridRows, modifiedRowId, removedElement, removedColumn };
};

/** Removes all empty rows from a set of `GridRow`s */
export const removeEmptyRows = (
  gridRows: readonly GridRow[],
): { newGridRows: GridRow[]; removedGridRowIds: Set<GridRowId> } => {
  const [newGridRows, removedGridRows] = partition(
    gridRows,
    (row) => row.gridColumns.length > 0,
  );
  return {
    newGridRows,
    removedGridRowIds: new Set(removedGridRows.map((r) => r.id)),
  };
};

/** Removes a list of elements, any resulting empty columns, and any resulting empty rows */
export const removeElementsFully = (
  gridRows: readonly GridRow[],
  elementsToRemove: GridElementLocator[],
): {
  newGridRows: readonly GridRow[];
  modifiedGridRowIds: Set<GridRowId>;
  removedGridRowIds: Set<GridRowId>;
} => {
  let tempGridRows: readonly GridRow[] = gridRows;
  const modifiedGridRowIds = new Set<GridRowId>();
  elementsToRemove.forEach((locator) => {
    const result = removeElementAndEmptyColumn(tempGridRows, locator);
    tempGridRows = result.newGridRows;
    if (result.modifiedRowId != null) {
      modifiedGridRowIds.add(result.modifiedRowId);
    }
  });

  const { newGridRows, removedGridRowIds } = removeEmptyRows(tempGridRows);
  return {
    newGridRows,
    modifiedGridRowIds: setDifference(modifiedGridRowIds, removedGridRowIds),
    removedGridRowIds,
  };
};
